import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import { getDownloadExtension } from '../utils/downloadFilename';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import usePermissions from '../hooks/usePermissions';
import { Eye, Edit, UserPlus, FileSpreadsheet, Users, Plus, CheckCircle2 } from 'lucide-react';
import PermissionDeniedCard from '../components/PermissionDeniedCard';
import ProfilePhotoCropModal from '../components/ProfilePhotoCropModal';
import AuthenticatedImage from '../components/AuthenticatedImage';
import { evaluateProfileCompletion } from '../utils/profileCompletion';

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = rawBackendUrl ? rawBackendUrl.replace(/\/$/, '') : 'http://api.acbase.org';
const getBackendMediaUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/api/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/')) return `${BACKEND_URL}/api${path}`;
  return `${BACKEND_URL}/api/${path}`;
};

const memberToSearchableText = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(memberToSearchableText).join(' ');
  if (typeof value === 'object') return Object.values(value).map(memberToSearchableText).join(' ');
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

export default function MembersPage() {
  const { user } = useContext(AuthContext);
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canViewAllMembers = hasPermission('USER_VIEW_ALL');
  const canViewMembers = canViewAllMembers || hasPermission('USER_VIEW_PUBLIC');
  
  const [members, setMembers] = useState([]);
  // Removed filteredMembers state - now using useMemo filteredMembersList
  const [searchTerm, setSearchTerm] = useState('');
  const [filterJobCategory, setFilterJobCategory] = useState('all');
  const [filterCompanySector, setFilterCompanySector] = useState('all');
  const [filterCustomRole, setFilterCustomRole] = useState('all');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [votingNotificationPromptOpen, setVotingNotificationPromptOpen] = useState(false);
  const [pendingVotingNotificationMember, setPendingVotingNotificationMember] = useState(null);
  const [sendingVotingNotification, setSendingVotingNotification] = useState(false);
  
  // Form data for modal
  const [formData, setFormData] = useState({});
  const [memberConsents, setMemberConsents] = useState([]);
  const [memberAccessLogs, setMemberAccessLogs] = useState([]);
  
  // Reference data
  const [countries, setCountries] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [companySectors, setCompanySectors] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  
  // Create mode
  const [createMode, setCreateMode] = useState(false);
  const isReadOnlyMemberModal = !createMode && !editMode;
  const isEditUserModal = !createMode && editMode;

  // Quote Associative state
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedMemberForQuote, setSelectedMemberForQuote] = useState(null);
  const [quote, setQuote] = useState([]);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [groups, setGroups] = useState([]);
  const getInitialQuoteFormData = (memberId = '') => ({
    data_inizio: '',
    data_fine: '',
    prezzo: '',
    pagato: false,
    gruppo_id: '',
    user_id: memberId
  });
  const [quoteFormData, setQuoteFormData] = useState(getInitialQuoteFormData());
  const [editingQuota, setEditingQuota] = useState(null);
  const [showQuotaForm, setShowQuotaForm] = useState(false);
  const [refundingQuotaId, setRefundingQuotaId] = useState(null);
  
  // Password reset state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // CV management state
  const [cvFile, setCvFile] = useState(null);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [deletingCv, setDeletingCv] = useState(false);
  const [motivationLetterFile, setMotivationLetterFile] = useState(null);
  const [uploadingMotivationLetter, setUploadingMotivationLetter] = useState(false);
  const [deletingMotivationLetter, setDeletingMotivationLetter] = useState(false);
  const [adhesionLetterFile, setAdhesionLetterFile] = useState(null);
  const [uploadingAdhesionLetter, setUploadingAdhesionLetter] = useState(false);
  const [deletingAdhesionLetter, setDeletingAdhesionLetter] = useState(false);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);
  const [deletingProfilePhoto, setDeletingProfilePhoto] = useState(false);
  const [profilePhotoVersion, setProfilePhotoVersion] = useState(0);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawProfilePhotoFile, setRawProfilePhotoFile] = useState(null);

  const handleDownloadCv = async (memberId, memberName, cvPath = '') => {
    try {
      const response = await axios.get(`/members/${memberId}/cv`, {
        responseType: 'blob'
      });
      const fileExtension = getDownloadExtension({
        filePath: cvPath,
        contentDisposition: response.headers?.['content-disposition']
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CV_${memberName}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download CV', error);
      toast.error(getErrorMessage(error, 'Errore nel download del CV'));
    }
  };

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }
    if (!canViewMembers) {
      setLoading(false);
      return;
    }
    fetchMembers();
    fetchReferenceData();
  }, [permissionsLoading, canViewMembers]);

  // Use useMemo instead of useEffect for filtering
  const filteredMembersList = useMemo(() => {
    let filtered = members;

    // Text search filter
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    if (normalizedSearchTerm) {
      filtered = filtered.filter((member) => {
        const searchableText = memberToSearchableText(member).toLowerCase();
        return searchableText.includes(normalizedSearchTerm);
      });
    }

    // Job category filter
    if (filterJobCategory && filterJobCategory !== 'all') {
      filtered = filtered.filter((member) => member.job_category_id?.toString() === filterJobCategory);
    }

    // Company sector filter
    if (filterCompanySector && filterCompanySector !== 'all') {
      filtered = filtered.filter((member) => member.company_sector_id?.toString() === filterCompanySector);
    }

    // Custom role filter
    if (filterCustomRole && filterCustomRole !== 'all') {
      filtered = filtered.filter((member) => (member.custom_role || '') === filterCustomRole);
    }

    return filtered;
  }, [searchTerm, filterJobCategory, filterCompanySector, filterCustomRole, members]);

  const fetchReferenceData = async () => {
    try {
      const [countriesRes, jobCatRes, compSecRes, customRolesRes] = await Promise.all([
        axios.get('/countries'),
        axios.get('/job-categories'),
        axios.get('/company-sectors'),
        axios.get('/custom-roles')
      ]);
      setCountries(countriesRes.data);
      setJobCategories(jobCatRes.data);
      setCompanySectors(compSecRes.data);
      setCustomRoles(customRolesRes.data);
    } catch (error) {
      console.error('Failed to fetch reference data', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get('/members');
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members', error);
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per visualizzare i membri');
      }
    } finally {
      setLoading(false);
    }
  };

  const mapMemberToFormData = useCallback(
    (member) => ({
      ...member,
      city_strgin: member?.city_strgin || member?.city || '',
      utente_invitato_da: member?.utente_invitato_da || ''
    }),
    []
  );

  const openCreateModal = () => {
    setCreateMode(true);
    setEditMode(true);
    setSelectedMember(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone_prefix: '+39',
      phone: '',
      country_id: null,
      province_id: null,
      city_id: null,
      city_strgin: '',
      utente_invitato_da: '',
      residence_address: '',
      company_name: '',
      job_title: '',
      job_category_id: null,
      company_sector_id: null,
      custom_role_id: null,
      linkedin_url: '',
      additional_info: '',
      private_notes: '',
      role: 'member_base',
      auth_method: 'password',
      in_voting: false,
      is_public_visible: true,
      profile_visibility: {
        email: true,
        phone: true,
        country: true,
        province: true,
        city: true,
        residence_address: true,
        company_name: true,
        job_title: true,
        job_category_id: true,
        company_sector_id: true,
        linkedin_url: true
      }
    });
    setCvFile(null);
    setMotivationLetterFile(null);
    setAdhesionLetterFile(null);
    setMemberConsents([]);
    setMemberAccessLogs([]);
    setModalOpen(true);
  };

  const openMemberModal = async (memberId, isEditMode) => {
    try {
      const [memberResponse, consentsResponse, accessLogsResponse] = await Promise.all([
        axios.get(`/members/${memberId}`, {
          params: { include_private_notes: isEditMode }
        }),
        axios.get(`/members/${memberId}/consents`),
        axios.get(`/members/${memberId}/access-logins`, {
          params: { limit: 10 }
        })
      ]);
      setSelectedMember(memberResponse.data);
      setFormData(mapMemberToFormData(memberResponse.data));
      setMemberConsents(consentsResponse.data?.consents || []);
      setMemberAccessLogs(accessLogsResponse.data?.items || []);
      setCreateMode(false);
      setEditMode(isEditMode);
      setCvFile(null);
      setMotivationLetterFile(null);
      setAdhesionLetterFile(null);
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
      setProfilePhotoVersion(Date.now());
      
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch member details', error);
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per visualizzare i dettagli');
      } else {
        toast.error('Errore nel caricamento dei dettagli');
      }
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Keep retired geo foreign keys cleared when country changes.
    if (field === 'country_id') {
      setFormData(prev => ({ ...prev, province_id: null, city_id: null }));
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Nome, cognome ed email sono obbligatori');
      return;
    }
    
    if (createMode && !formData.phone) {
      toast.error('Telefono obbligatorio');
      return;
    }
    if (!isEditUserModal && !String(formData.city_strgin || '').trim()) {
      toast.error('Città obbligatoria');
      return;
    }
    
    setSaving(true);
    const shouldPromptVotingNotification = !createMode
      && !Boolean(selectedMember?.in_voting)
      && Boolean(formData.in_voting);
    const updatedMemberId = selectedMember?.id;
    const updatedMemberName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim();
    try {
      if (createMode) {
        const response = await axios.post('/members', formData);
        toast.success('Utente creato con successo');
        if (response.data.temp_password) {
          toast.info(`Password temporanea: ${response.data.temp_password}`);
        }
      } else {
        await axios.put(`/members/${selectedMember.id}`, formData);
        await axios.put(`/members/${selectedMember.id}/consents`, {
          consents: memberConsents.map((consent) => ({
            consent_id: consent.id,
            accepted: !!consent.accepted
          }))
        });
        toast.success('Utente aggiornato con successo');
      }
      setModalOpen(false);
      if (shouldPromptVotingNotification && updatedMemberId) {
        setPendingVotingNotificationMember({
          id: updatedMemberId,
          name: updatedMemberName
        });
        setVotingNotificationPromptOpen(true);
      }
      await fetchMembers();
    } catch (error) {
      console.error('Failed to save member', error);
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendVotingNotification = async () => {
    if (!pendingVotingNotificationMember?.id) {
      setVotingNotificationPromptOpen(false);
      setPendingVotingNotificationMember(null);
      return;
    }

    setSendingVotingNotification(true);
    try {
      const response = await axios.post(`/members/${pendingVotingNotificationMember.id}/notify-voting-start`);
      const queuedCount = response.data?.queued_count;
      if (typeof queuedCount === 'number') {
        toast.success(`Notifica inviata: ${queuedCount} email accodate`);
      } else {
        toast.success('Notifiche accodate con successo');
      }
    } catch (error) {
      console.error('Failed to send voting notifications', error);
      toast.error(getErrorMessage(error, 'Errore nell\'invio notifiche'));
    } finally {
      setSendingVotingNotification(false);
      setVotingNotificationPromptOpen(false);
      setPendingVotingNotificationMember(null);
    }
  };

  const handleConsentToggle = (consentId, checked) => {
    setMemberConsents((prev) =>
      prev.map((consent) =>
        consent.id === consentId
          ? { ...consent, accepted: checked === true }
          : consent
      )
    );
  };

  const handleResetPassword = async () => {
    // Validation
    if (!newPassword || !confirmPassword) {
      toast.error('Inserisci la nuova password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.post(`/members/${selectedMember.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reimpostata con successo');
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to reset password', error);
      toast.error(getErrorMessage(error, 'Errore nel reset password'));
    } finally {
      setResettingPassword(false);
    }
  };

  const handleUploadCv = async () => {
    if (!cvFile) {
      toast.error('Seleziona un file');
      return;
    }
    
    setUploadingCv(true);
    try {
      const formData = new FormData();
      formData.append('cv', cvFile);
      
      await axios.post(`/members/${selectedMember.id}/cv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('CV caricato con successo');
      setCvFile(null);
      
      // Refresh member data
      const response = await axios.get(`/members/${selectedMember.id}`);
      setSelectedMember(response.data);
      setFormData(mapMemberToFormData(response.data));
    } catch (error) {
      console.error('Failed to upload CV', error);
      toast.error(getErrorMessage(error, 'Errore nel caricamento del CV'));
    } finally {
      setUploadingCv(false);
    }
  };

  const handleDeleteCv = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare il CV?')) {
      return;
    }
    
    setDeletingCv(true);
    try {
      await axios.delete(`/members/${selectedMember.id}/cv`);
      toast.success('CV eliminato con successo');
      
      // Refresh member data
      const response = await axios.get(`/members/${selectedMember.id}`);
      setSelectedMember(response.data);
      setFormData(mapMemberToFormData(response.data));
    } catch (error) {
      console.error('Failed to delete CV', error);
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione del CV'));
    } finally {
      setDeletingCv(false);
    }
  };

  const handleDownloadMotivationLetter = async (memberId, memberName, motivationLetterPath = '') => {
    try {
      const response = await axios.get(`/members/${memberId}/motivation-letter`, {
        responseType: 'blob'
      });
      const fileExtension = getDownloadExtension({
        filePath: motivationLetterPath,
        contentDisposition: response.headers?.['content-disposition']
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lettera_Motivazionale_${memberName}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download motivation letter', error);
      toast.error(getErrorMessage(error, 'Errore nel download della lettera motivazionale'));
    }
  };

  const handleUploadMotivationLetter = async () => {
    if (!motivationLetterFile) {
      toast.error('Seleziona un file');
      return;
    }

    setUploadingMotivationLetter(true);
    try {
      const formData = new FormData();
      formData.append('file', motivationLetterFile);

      await axios.post(`/members/${selectedMember.id}/motivation-letter`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Lettera motivazionale caricata con successo');
      setMotivationLetterFile(null);

      const response = await axios.get(`/members/${selectedMember.id}`);
      setSelectedMember(response.data);
      setFormData(mapMemberToFormData(response.data));
    } catch (error) {
      console.error('Failed to upload motivation letter', error);
      toast.error(getErrorMessage(error, 'Errore nel caricamento della lettera motivazionale'));
    } finally {
      setUploadingMotivationLetter(false);
    }
  };

  const handleDeleteMotivationLetter = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare la lettera motivazionale?')) {
      return;
    }

    setDeletingMotivationLetter(true);
    try {
      await axios.delete(`/members/${selectedMember.id}/motivation-letter`);
      toast.success('Lettera motivazionale eliminata con successo');

      const response = await axios.get(`/members/${selectedMember.id}`);
      setSelectedMember(response.data);
      setFormData(mapMemberToFormData(response.data));
    } catch (error) {
      console.error('Failed to delete motivation letter', error);
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione della lettera motivazionale'));
    } finally {
      setDeletingMotivationLetter(false);
    }
  };

  const handleDownloadAdhesionLetter = async (memberId, memberName, adhesionLetterPath = '') => {
    try {
      const response = await axios.get(`/members/${memberId}/adhesion-letter`, {
        responseType: 'blob'
      });
      const fileExtension = getDownloadExtension({
        filePath: adhesionLetterPath,
        contentDisposition: response.headers?.['content-disposition']
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lettera_Adesione_${memberName}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download adhesion letter', error);
      toast.error(getErrorMessage(error, 'Errore nel download della lettera di adesione'));
    }
  };

  const handleUploadAdhesionLetter = async () => {
    if (!adhesionLetterFile) {
      toast.error('Seleziona un file');
      return;
    }

    setUploadingAdhesionLetter(true);
    try {
      const formData = new FormData();
      formData.append('file', adhesionLetterFile);

      await axios.post(`/members/${selectedMember.id}/adhesion-letter`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Lettera di adesione caricata con successo');
      setAdhesionLetterFile(null);

      const response = await axios.get(`/members/${selectedMember.id}`);
      setSelectedMember(response.data);
      setFormData(mapMemberToFormData(response.data));
    } catch (error) {
      console.error('Failed to upload adhesion letter', error);
      toast.error(getErrorMessage(error, 'Errore nel caricamento della lettera di adesione'));
    } finally {
      setUploadingAdhesionLetter(false);
    }
  };

  const handleDeleteAdhesionLetter = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare la lettera di adesione?')) {
      return;
    }

    setDeletingAdhesionLetter(true);
    try {
      await axios.delete(`/members/${selectedMember.id}/adhesion-letter`);
      toast.success('Lettera di adesione eliminata con successo');

      const response = await axios.get(`/members/${selectedMember.id}`);
      setSelectedMember(response.data);
      setFormData(mapMemberToFormData(response.data));
    } catch (error) {
      console.error('Failed to delete adhesion letter', error);
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione della lettera di adesione'));
    } finally {
      setDeletingAdhesionLetter(false);
    }
  };

  const handleUploadProfilePhoto = async (fileToUpload) => {
    if (!fileToUpload) {
      toast.error('File foto non valido');
      return;
    }

    setUploadingProfilePhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);

      await axios.post(`/members/${selectedMember.id}/profile-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Foto profilo caricata con successo');
      setProfilePhotoVersion(Date.now());

      const response = await axios.get(`/members/${selectedMember.id}`);
      setSelectedMember(response.data);
      setFormData(mapMemberToFormData(response.data));
      await fetchMembers();
    } catch (error) {
      console.error('Failed to upload profile photo', error);
      toast.error(getErrorMessage(error, 'Errore nel caricamento della foto'));
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  const handleDeleteProfilePhoto = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare la foto profilo?')) {
      return;
    }

    setDeletingProfilePhoto(true);
    try {
      await axios.delete(`/members/${selectedMember.id}/profile-photo`);
      toast.success('Foto profilo eliminata con successo');
      setProfilePhotoVersion(Date.now());

      const response = await axios.get(`/members/${selectedMember.id}`);
      setSelectedMember(response.data);
      setFormData(mapMemberToFormData(response.data));
      await fetchMembers();
    } catch (error) {
      console.error('Failed to delete profile photo', error);
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione della foto'));
    } finally {
      setDeletingProfilePhoto(false);
    }
  };

  const handleProfilePhotoInputChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = '';
    if (!selectedFile) {
      return;
    }
    setRawProfilePhotoFile(selectedFile);
    setCropModalOpen(true);
  };

  const handleCropConfirm = async (croppedFile) => {
    setRawProfilePhotoFile(null);
    setCropModalOpen(false);
    await handleUploadProfilePhoto(croppedFile);
  };

  // Quote Associative functions
  const openQuoteModal = async (member) => {
    setSelectedMemberForQuote(member);
    setQuote([]);
    setEditingQuota(null);
    setShowQuotaForm(false);
    setQuoteFormData(getInitialQuoteFormData(member.id));
    setQuoteModalOpen(true);
    await fetchQuote(member.id);
    await fetchGroups();
  };

  const fetchQuote = async (memberId) => {
    const targetMemberId = memberId || selectedMemberForQuote?.id;
    if (!targetMemberId) return;
    setLoadingQuote(true);
    try {
      const response = await axios.get(`/quote-associative/${targetMemberId}`);
      setQuote(response.data);
    } catch (error) {
      console.error('Failed to fetch quote', error);
      toast.error('Errore nel caricamento delle quote');
    } finally {
      setLoadingQuote(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get('/groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch groups', error);
    }
  };

  const handleCreateQuota = async () => {
    if (!quoteFormData.user_id || !quoteFormData.data_inizio || !quoteFormData.data_fine || !quoteFormData.prezzo || !quoteFormData.gruppo_id) {
      toast.error('Tutti i campi sono obbligatori');
      return;
    }

    try {
      const payload = { ...quoteFormData };
      if (editingQuota?.has_stripe_session || editingQuota?.stripe_payment?.stripe_checkout_session_id) {
        delete payload.pagato;
      }

      if (editingQuota) {
        await axios.put(`/quote-associative/${editingQuota.id}`, payload);
        toast.success('Quota aggiornata con successo');
      } else {
        await axios.post('/quote-associative', payload);
        toast.success('Quota creata con successo');
      }
      setQuoteFormData(getInitialQuoteFormData(selectedMemberForQuote?.id));
      setEditingQuota(null);
      await fetchQuote(selectedMemberForQuote?.id);
      await fetchMembers();
    } catch (error) {
      console.error('Failed to save quota', error);
      toast.error(getErrorMessage(error, 'Errore nel salvataggio della quota'));
    }
  };

  const handleEditQuota = (quota) => {
    setEditingQuota(quota);
    setShowQuotaForm(true);
    setQuoteFormData({
      data_inizio: quota.data_inizio,
      data_fine: quota.data_fine,
      prezzo: quota.prezzo,
      pagato: quota.pagato,
      gruppo_id: quota.gruppo_id,
      user_id: selectedMemberForQuote?.id || ''
    });
  };

  const isEditingPaidQuota = Boolean(editingQuota?.pagato);

  const handleDeleteQuota = async (quotaId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa quota?')) return;

    try {
      await axios.delete(`/quote-associative/${quotaId}`);
      toast.success('Quota eliminata con successo');
      await fetchQuote(selectedMemberForQuote?.id);
      await fetchMembers();
    } catch (error) {
      console.error('Failed to delete quota', error);
      toast.error('Errore nell\'eliminazione della quota');
    }
  };

  const hasStripeSession = (quotaItem) => Boolean(
    quotaItem?.has_stripe_session || quotaItem?.stripe_payment?.stripe_checkout_session_id
  );

  const isStripeRefunded = (quotaItem) => {
    const stripePayment = quotaItem?.stripe_payment;
    if (!stripePayment) return false;
    const normalizedStatus = (stripePayment.status || '').toLowerCase();
    const normalizedRefundStatus = (stripePayment.stripe_refund_status || '').toLowerCase();
    return Boolean(
      stripePayment.is_refunded
      || stripePayment.is_fully_refunded
      || normalizedStatus === 'refunded'
      || normalizedStatus === 'partially_refunded'
      || normalizedRefundStatus === 'refunded'
      || normalizedRefundStatus === 'partially_refunded'
    );
  };

  const canRefundQuota = (quotaItem) => Boolean(
    hasPermission('USER_EDIT_QUOTE')
    && quotaItem?.pagato
    && hasStripeSession(quotaItem)
    && !isStripeRefunded(quotaItem)
    && quotaItem?.stripe_payment?.stripe_payment_intent_id
  );

  const canDeleteQuota = (quotaItem) => Boolean(
    hasPermission('USER_EDIT_QUOTE')
    && !quotaItem?.pagato
    && (!hasStripeSession(quotaItem) || isStripeRefunded(quotaItem))
  );

  const getPaymentLabel = (quotaItem) => {
    if (!quotaItem?.pagato) return null;
    return hasStripeSession(quotaItem) ? 'Pagato con Stripe' : 'Pagato manualmente';
  };

  const getQuoteButtonClassName = (member) => {
    if (member?.has_valid_paid_quota) {
      return 'border-green-600 bg-green-600 text-white hover:bg-green-700';
    }

    if (member?.has_valid_unpaid_quota) {
      return 'border-red-600 bg-red-600 text-white hover:bg-red-700';
    }

    return 'border-green-600 bg-white text-green-600 hover:bg-green-50';
  };

  const getInitials = (member) => {
    const first = member?.first_name?.[0] || '';
    const last = member?.last_name?.[0] || '';
    return `${first}${last}`.toUpperCase() || 'U';
  };

  const formatLoginDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('it-IT');
  };

  const handleRefundQuota = async (quotaItem) => {
    if (!quotaItem?.id) return;
    if (!window.confirm('Confermi il rimborso Stripe di questa quota?')) return;

    setRefundingQuotaId(quotaItem.id);
    try {
      const response = await axios.post(`/quote-associative/${quotaItem.id}/refund`);
      toast.success(response.data?.message || 'Rimborso emesso con successo');
      await fetchQuote(selectedMemberForQuote?.id);
      await fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore durante il rimborso della quota'));
    } finally {
      setRefundingQuotaId(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get('/members/export/excel', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'membri_associazione.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export Excel completato con successo!');
    } catch (error) {
      console.error('Failed to export Excel:', error);
      toast.error(getErrorMessage(error, 'Errore durante l\'export Excel'));
    }
  };

  if (permissionsLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-20 px-6 pb-6">Caricamento...</div>
      </div>
    );
  }

  if (!canViewMembers) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <PermissionDeniedCard message="Non hai i permessi per visualizzare i membri." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="pt-20 px-4 pb-6 sm:px-6 sm:pt-20 sm:pb-6 max-w-7xl mx-auto">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-6 w-6" />
              <h1 className="text-2xl sm:text-3xl font-bold">Database membri</h1>
            </div>
            <p className="text-gray-600">Gestisci e visualizza i membri dell&apos;associazione</p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            {hasPermission('MEMBER_EXPORT') && (
              <Button variant="outline" onClick={handleExportExcel} className="w-full sm:w-auto">
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Esporta Excel
              </Button>
            )}
            {hasPermission('USER_ADD') && (
              <Button onClick={openCreateModal} className="w-full sm:w-auto">
                <UserPlus className="mr-2 h-4 w-4" />
                Nuovo membro
              </Button>
            )}
          </div>
        </div>

        {user?.is_public_visible === false && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-1">Profilo privato</h3>
            <p className="text-sm text-blue-800">
              Il tuo profilo è impostato come privato e non è visibile ai membri base ordinari, ma solo al consiglio direttivo
            </p>
          </div>
        )}

        <div className="mb-6 space-y-4">
          <Input
            placeholder="Cerca membri su tutti i campi disponibili..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:max-w-md border-2 border-gray-300 focus:border-black"
          />
          
          <div className="flex gap-4 flex-wrap">
            <Select value={filterJobCategory} onValueChange={setFilterJobCategory}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue placeholder="Filtra per Categoria Professionale" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {jobCategories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id.toString()}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterCustomRole} onValueChange={setFilterCustomRole}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue placeholder="Filtra per Ruolo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i ruoli</SelectItem>
                {customRoles.map(role => (
                  <SelectItem key={role.id} value={role.name}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterCompanySector} onValueChange={setFilterCompanySector}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue placeholder="Filtra per Settore Azienda" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i settori</SelectItem>
                {companySectors.map(sector => (
                  <SelectItem key={sector.id} value={sector.id.toString()}>
                    {sector.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembersList.map((member) => {
              const isMemberProfileComplete = evaluateProfileCompletion(member).isComplete;
              return (
                <Card key={member.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        <div className="h-14 w-14 rounded-full overflow-hidden border bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600">
                          {member.profile_photo_url ? (
                            <AuthenticatedImage
                              src={`${getBackendMediaUrl(member.profile_photo_url)}?v=${profilePhotoVersion}`}
                              alt={`${member.first_name} ${member.last_name}`}
                              className="h-full w-full object-cover"
                              fallback={<span>{getInitials(member)}</span>}
                            />
                          ) : (
                            <span>{getInitials(member)}</span>
                          )}
                        </div>
                        <div>
                          <div className="text-xl">{member.first_name} {member.last_name}</div>
                          <div className="flex gap-2 mt-2">
                            {member.custom_role && (
                              <span className="inline-block px-2 py-1 text-xs bg-indigo-600 text-white rounded">
                                {member.custom_role}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canViewAllMembers && isMemberProfileComplete && (
                          <span
                            title="Profilo utente completo"
                            aria-label="Profilo utente completo"
                            className="text-emerald-600"
                          >
                            <CheckCircle2 className="h-5 w-5" />
                          </span>
                        )}
                        {!member.is_public_visible && (
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Privato</span>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {/* Email e Telefono */}
                      {member.email && (
                        <p className="text-sm text-gray-700 font-medium">
                          📧 {member.email}
                        </p>
                      )}
                      {member.phone && (
                        <p className="text-sm text-gray-700 font-medium">
                          📱 {member.phone_prefix} {member.phone}
                        </p>
                      )}
                      
                      {/* Località */}
                      {member.city && (
                        <p className="text-sm text-gray-600">
                          📍 {member.country ? `${member.country}, ${member.city}` : member.city}
                        </p>
                      )}
                      {member.residence_address && (
                        <p className="text-sm text-gray-600">
                          🏠 {member.residence_address}
                        </p>
                      )}
                      
                      {/* Informazioni Professionali */}
                      {(member.company_name || member.job_title || member.job_category_name || member.company_sector_name) && (
                        <div className="pt-2 border-t border-gray-200 space-y-1">
                          {member.company_name && (
                            <p className="text-sm text-gray-700 font-medium">
                              🏢 {member.company_name}
                            </p>
                          )}
                          {member.company_sector_name && (
                            <p className="text-sm text-gray-600">
                              🏭 {member.company_sector_name}
                            </p>
                          )}
                          {member.job_title && (
                            <p className="text-sm text-gray-600">
                              👔 {member.job_title}
                            </p>
                          )}
                          {member.job_category_name && (
                            <p className="text-sm text-gray-600">
                              💼 {member.job_category_name}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Link LinkedIn */}
                      {member.linkedin_url && (
                        <div className="pt-2">
                          <a 
                            href={member.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                          >
                            🔗 LinkedIn
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </div>
                      )}
                      
                      {/* Link CV */}
                      {member.cv_path && (
                        <div className="pt-2">
                          <button
                            onClick={() => handleDownloadCv(member.id, `${member.first_name}_${member.last_name}`, member.cv_path)}
                            className="text-sm text-green-600 hover:text-green-800 hover:underline flex items-center gap-1 bg-transparent border-0 cursor-pointer"
                          >
                            📄 Scarica CV
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                        </div>
                      )}
                      
                      {/* Altro (Associazioni, Certificazioni, etc.) */}
                      {member.additional_info && (
                        <div className="pt-2 border-t border-gray-200">
                          <p className="text-sm text-gray-600 whitespace-pre-line">
                            📋 <strong>Altro:</strong> {member.additional_info}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 flex gap-2 flex-wrap">
                      {hasPermission('USER_VIEW_ALL') && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openMemberModal(member.id, false)}
                        >
                          <Eye className="mr-1 h-4 w-4" />
                          Visualizza
                        </Button>
                      )}
                      {(hasPermission('USER_MANAGE')) && (
                        <Button 
                          size="sm"
                          onClick={() => openMemberModal(member.id, true)}
                        >
                          <Edit className="mr-1 h-4 w-4" />
                          Modifica
                        </Button>
                      )}
                      {(hasPermission('USER_VIEW_QUOTE') || hasPermission('USER_EDIT_QUOTE')) && (
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => openQuoteModal(member)}
                          className={getQuoteButtonClassName(member)}
                        >
                          💶 Quote
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {!loading && filteredMembersList.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Nessun membro trovato
          </div>
        )}
      </div>

      {/* Member Details Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {createMode ? 'Nuovo utente' : editMode ? 'Modifica utente' : 'Dettagli membro'}
            </DialogTitle>
            <DialogDescription>
              {selectedMember && `${selectedMember.first_name} ${selectedMember.last_name}`}
              {createMode && 'Inserisci i dati del nuovo utente'}
            </DialogDescription>
          </DialogHeader>

          {(selectedMember || createMode) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              {/* Personal Info */}
              <div className="sm:col-span-2">
                <h3 className="font-semibold text-lg mb-3">Informazioni personali</h3>
              </div>
              
              <div>
                <Label>Nome</Label>
                <Input
                  value={formData.first_name || ''}
                  onChange={(e) => handleFieldChange('first_name', e.target.value)}
                  disabled={!editMode}
                />
              </div>
              
              <div>
                <Label>Cognome</Label>
                <Input
                  value={formData.last_name || ''}
                  onChange={(e) => handleFieldChange('last_name', e.target.value)}
                  disabled={!editMode}
                />
              </div>
              
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <Label>Email</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="visibility-email"
                      checked={formData.profile_visibility?.email || false}
                      onCheckedChange={(checked) => handleFieldChange('profile_visibility', {...formData.profile_visibility, email: checked})}
                      disabled={!editMode}
                    />
                    <Label htmlFor="visibility-email" className="text-xs text-gray-600">Visibile su card</Label>
                  </div>
                </div>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  disabled={!editMode}
                />
              </div>
              
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <Label>Telefono</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="visibility-phone"
                      checked={formData.profile_visibility?.phone || false}
                      onCheckedChange={(checked) => handleFieldChange('profile_visibility', {...formData.profile_visibility, phone: checked})}
                      disabled={!editMode}
                    />
                    <Label htmlFor="visibility-phone" className="text-xs text-gray-600">Visibile su card</Label>
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={formData.phone_prefix || ''}
                    onChange={(e) => handleFieldChange('phone_prefix', e.target.value)}
                    disabled={!editMode}
                    className="w-full sm:w-20"
                  />
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    disabled={!editMode}
                  />
                </div>
              </div>

              {/* Location */}
              <div className="sm:col-span-2">
                <h3 className="font-semibold text-lg mb-3 mt-4">Località</h3>
              </div>
              
              <div>
                <Label>Paese</Label>
                <Select
                  value={formData.country_id?.toString() || ''}
                  onValueChange={(value) => handleFieldChange('country_id', parseInt(value))}
                  disabled={!editMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona paese" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(country => (
                      <SelectItem key={country.id} value={country.id.toString()}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <Label>
                    Città
                    {!isEditUserModal && <span> *</span>}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="visibility-city"
                      checked={formData.profile_visibility?.city || false}
                      onCheckedChange={(checked) => handleFieldChange('profile_visibility', {...formData.profile_visibility, city: checked, province: checked, country: checked})}
                      disabled={!editMode}
                    />
                    <Label htmlFor="visibility-city" className="text-xs text-gray-600">Visibile su card</Label>
                  </div>
                </div>
                <Input
                  value={formData.city_strgin || ''}
                  onChange={(e) => handleFieldChange('city_strgin', e.target.value)}
                  disabled={!editMode}
                  placeholder="Inserisci città"
                />
              </div>

              {/* Professional Info */}
              <div className="sm:col-span-2">
                <h3 className="font-semibold text-lg mb-3 mt-4">Informazioni professionali</h3>
              </div>
              
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <Label>Nome Azienda</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="visibility-company"
                      checked={formData.profile_visibility?.company_name || false}
                      onCheckedChange={(checked) => handleFieldChange('profile_visibility', {...formData.profile_visibility, company_name: checked})}
                      disabled={!editMode}
                    />
                    <Label htmlFor="visibility-company" className="text-xs text-gray-600">Visibile su card</Label>
                  </div>
                </div>
                <Input
                  value={formData.company_name || ''}
                  onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  disabled={!editMode}
                />
              </div>
              
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <Label>Ruolo aziendale</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="visibility-job-title"
                      checked={formData.profile_visibility?.job_title || false}
                      onCheckedChange={(checked) => handleFieldChange('profile_visibility', {...formData.profile_visibility, job_title: checked})}
                      disabled={!editMode}
                    />
                    <Label htmlFor="visibility-job-title" className="text-xs text-gray-600">Visibile su card</Label>
                  </div>
                </div>
                <Input
                  value={formData.job_title || ''}
                  onChange={(e) => handleFieldChange('job_title', e.target.value)}
                  disabled={!editMode}
                />
              </div>
              
              <div>
                <Label>Categoria professionale</Label>
                <Select
                  value={formData.job_category_id?.toString() || ''}
                  onValueChange={(value) => handleFieldChange('job_category_id', parseInt(value))}
                  disabled={!editMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Settore Azienda *</Label>
                <Select
                  value={formData.company_sector_id?.toString() || ''}
                  onValueChange={(value) => handleFieldChange('company_sector_id', parseInt(value))}
                  disabled={!editMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona settore" />
                  </SelectTrigger>
                  <SelectContent>
                    {companySectors.map(sector => (
                      <SelectItem key={sector.id} value={sector.id.toString()}>
                        {sector.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Ruolo (opzionale)</Label>
                <Select
                  value={formData.custom_role_id ? formData.custom_role_id.toString() : 'none'}
                  onValueChange={(value) => handleFieldChange('custom_role_id', value === 'none' ? null : parseInt(value))}
                  disabled={!editMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nessun ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun ruolo</SelectItem>
                    {customRoles.map(role => (
                      <SelectItem key={role.id} value={role.id.toString()}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <Label>LinkedIn URL</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="visibility-linkedin"
                      checked={formData.profile_visibility?.linkedin_url || false}
                      onCheckedChange={(checked) => handleFieldChange('profile_visibility', {...formData.profile_visibility, linkedin_url: checked})}
                      disabled={!editMode}
                    />
                    <Label htmlFor="visibility-linkedin" className="text-xs text-gray-600">Visibile su card</Label>
                  </div>
                </div>
                <Input
                  value={formData.linkedin_url || ''}
                  onChange={(e) => handleFieldChange('linkedin_url', e.target.value)}
                  disabled={!editMode}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>Altro (associazioni, certificazioni, studi,...)</Label>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Es: Membro di Associazione XYZ, Certificazione ABC, Laurea in..."
                  value={formData.additional_info || ''}
                  onChange={(e) => handleFieldChange('additional_info', e.target.value)}
                  disabled={!editMode}
                />
              </div>

              <div className="sm:col-span-2 border-t pt-4">
                <Label>Note private (solo amministrazione)</Label>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Queste note sono visibili solo qui da parte degli amministratori."
                  value={formData.private_notes || ''}
                  onChange={(e) => handleFieldChange('private_notes', e.target.value)}
                  disabled={!editMode}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>Utente invitato da</Label>
                <Input
                  value={formData.utente_invitato_da || ''}
                  onChange={(e) => handleFieldChange('utente_invitato_da', e.target.value)}
                  disabled={!editMode}
                  placeholder="Nome e cognome del referente"
                />
              </div>

              {/* Profile Photo Section */}
              {!createMode && (
                <>
                  <div className="sm:col-span-2 border-t pt-4">
                    <h3 className="font-semibold text-lg mb-3">Foto profilo</h3>
                  </div>

                  <div className="sm:col-span-2 space-y-3">
                    {selectedMember?.profile_photo_url ? (
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-3">
                            <AuthenticatedImage
                              src={`${getBackendMediaUrl(selectedMember.profile_photo_url)}?v=${profilePhotoVersion}`}
                              alt={`${selectedMember.first_name} ${selectedMember.last_name}`}
                              className="h-14 w-14 rounded-full object-cover border"
                              fallback={<span>{getInitials(selectedMember)}</span>}
                            />
                            <span className="text-sm font-medium text-green-900">Foto presente</span>
                          </div>
                          <button
                            onClick={handleDeleteProfilePhoto}
                            disabled={deletingProfilePhoto || !editMode}
                            className="text-sm text-red-600 hover:text-red-800 underline disabled:opacity-50"
                          >
                            {deletingProfilePhoto ? 'Eliminazione...' : 'Elimina'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                        <div className="h-14 w-14 rounded-full border bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600">
                          {getInitials(selectedMember)}
                        </div>
                        <p className="text-sm text-gray-600">Nessuna foto profilo caricata</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="profile-photo-upload">Scegli foto profilo</Label>
                      <Input
                        id="profile-photo-upload"
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.gif"
                        onChange={handleProfilePhotoInputChange}
                        className="flex-1"
                        disabled={uploadingProfilePhoto || !editMode}
                      />
                      {uploadingProfilePhoto && <p className="text-sm text-gray-600">Caricamento in corso...</p>}
                      <p className="text-xs text-gray-500">Formati supportati: JPG, PNG, WEBP, GIF</p>
                    </div>
                  </div>
                </>
              )}

              {/* CV Section */}
              {!createMode && (
                <>
                  <div className="sm:col-span-2 border-t pt-4">
                    <h3 className="font-semibold text-lg mb-3">Curriculum vitae</h3>
                  </div>
                  
                  <div className="sm:col-span-2 space-y-3">
                    {/* CV Status */}
                    {selectedMember?.cv_path ? (
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-green-900">CV presente</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleDownloadCv(selectedMember.id, `${selectedMember.first_name}_${selectedMember.last_name}`, selectedMember.cv_path)}
                              className="text-sm text-blue-600 hover:text-blue-800 underline bg-transparent border-0 cursor-pointer"
                            >
                              Scarica
                            </button>
                            <button
                              onClick={handleDeleteCv}
                              disabled={deletingCv || !editMode}
                              className="text-sm text-red-600 hover:text-red-800 underline disabled:opacity-50"
                            >
                              {deletingCv ? 'Eliminazione...' : 'Elimina'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Nessun CV caricato</p>
                      </div>
                    )}
                    
                    {/* Upload New CV */}
                    <div className="space-y-2">
                      <Label htmlFor="cv-upload">
                        {selectedMember?.cv_path ? 'Sostituisci CV' : 'Carica CV'}
                      </Label>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                          id="cv-upload"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setCvFile(e.target.files[0])}
                          className="flex-1"
                          disabled={!editMode}
                        />
                        <Button
                          type="button"
                          onClick={handleUploadCv}
                          disabled={!editMode || !cvFile || uploadingCv}
                          className="w-full whitespace-nowrap sm:w-auto"
                        >
                          {uploadingCv ? 'Caricamento...' : 'Carica'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">Formati supportati: PDF, DOC, DOCX</p>
                    </div>
                    
                    {/* CV Visibility */}
                    <div className="flex items-center space-x-2 pt-2">
                      <Checkbox
                        id="cv-visible"
                        checked={formData.profile_visibility?.cv_path || false}
                        onCheckedChange={(checked) => handleFieldChange('profile_visibility', {...formData.profile_visibility, cv_path: checked})}
                        disabled={!editMode}
                      />
                      <Label htmlFor="cv-visible" className="text-sm">
                        Mostra CV sulla card pubblica
                      </Label>
                    </div>
                  </div>
                </>
              )}

              {/* Motivation Letter Section */}
              {!createMode && (
                <>
                  <div className="sm:col-span-2 border-t pt-4">
                    <h3 className="font-semibold text-lg mb-3">Lettera motivazionale</h3>
                  </div>

                  <div className="sm:col-span-2 space-y-3">
                    {selectedMember?.motivation_letter_path ? (
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-green-900">Lettera motivazionale presente</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleDownloadMotivationLetter(selectedMember.id, `${selectedMember.first_name}_${selectedMember.last_name}`, selectedMember.motivation_letter_path)}
                              className="text-sm text-blue-600 hover:text-blue-800 underline bg-transparent border-0 cursor-pointer"
                            >
                              Scarica
                            </button>
                            <button
                              onClick={handleDeleteMotivationLetter}
                              disabled={deletingMotivationLetter || !editMode}
                              className="text-sm text-red-600 hover:text-red-800 underline disabled:opacity-50"
                            >
                              {deletingMotivationLetter ? 'Eliminazione...' : 'Elimina'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Nessuna lettera motivazionale caricata</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="motivation-letter-upload">
                        {selectedMember?.motivation_letter_path ? 'Sostituisci lettera motivazionale' : 'Carica lettera motivazionale'}
                      </Label>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                          id="motivation-letter-upload"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setMotivationLetterFile(e.target.files[0])}
                          className="flex-1"
                          disabled={!editMode}
                        />
                        <Button
                          type="button"
                          onClick={handleUploadMotivationLetter}
                          disabled={!editMode || !motivationLetterFile || uploadingMotivationLetter}
                          className="w-full whitespace-nowrap sm:w-auto"
                        >
                          {uploadingMotivationLetter ? 'Caricamento...' : 'Carica'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">Formati supportati: PDF, DOC, DOCX</p>
                    </div>
                  </div>
                </>
              )}

              {/* Adhesion Letter Section */}
              {!createMode && (
                <>
                  <div className="sm:col-span-2 border-t pt-4">
                    <h3 className="font-semibold text-lg mb-3">Lettera di adesione</h3>
                  </div>

                  <div className="sm:col-span-2 space-y-3">
                    {selectedMember?.adhesion_letter_path ? (
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-green-900">Lettera di adesione presente</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleDownloadAdhesionLetter(selectedMember.id, `${selectedMember.first_name}_${selectedMember.last_name}`, selectedMember.adhesion_letter_path)}
                              className="text-sm text-blue-600 hover:text-blue-800 underline bg-transparent border-0 cursor-pointer"
                            >
                              Scarica
                            </button>
                            <button
                              onClick={handleDeleteAdhesionLetter}
                              disabled={deletingAdhesionLetter || !editMode}
                              className="text-sm text-red-600 hover:text-red-800 underline disabled:opacity-50"
                            >
                              {deletingAdhesionLetter ? 'Eliminazione...' : 'Elimina'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Nessuna lettera di adesione caricata</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="adhesion-letter-upload">
                        {selectedMember?.adhesion_letter_path ? 'Sostituisci lettera di adesione' : 'Carica lettera di adesione'}
                      </Label>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                          id="adhesion-letter-upload"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setAdhesionLetterFile(e.target.files[0])}
                          className="flex-1"
                          disabled={!editMode}
                        />
                        <Button
                          type="button"
                          onClick={handleUploadAdhesionLetter}
                          disabled={!editMode || !adhesionLetterFile || uploadingAdhesionLetter}
                          className="w-full whitespace-nowrap sm:w-auto"
                        >
                          {uploadingAdhesionLetter ? 'Caricamento...' : 'Carica'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">Formati supportati: PDF, DOC, DOCX</p>
                    </div>
                  </div>
                </>
              )}

              {!createMode && (
                <>
                  <div className="sm:col-span-2 border-t pt-4">
                    <h3 className="font-semibold text-lg mb-3">Consensi Privacy e Preferenze</h3>
                  </div>

                  <div className="sm:col-span-2 space-y-3">
                    {memberConsents.length === 0 && (
                      <p className="text-sm text-gray-500">Nessun consenso configurato</p>
                    )}
                    {memberConsents.map((consent) => (
                      <div key={consent.id} className="flex items-start space-x-3 border rounded-md p-3">
                        <Checkbox
                          id={`member-consent-${consent.id}`}
                          checked={!!consent.accepted}
                          onCheckedChange={(checked) => handleConsentToggle(consent.id, checked)}
                          disabled={!editMode}
                        />
                        <div>
                          <Label htmlFor={`member-consent-${consent.id}`} className="font-medium">
                            {consent.title}
                          </Label>
                          {consent.description && (
                            <p className="text-sm text-gray-500 mt-1">{consent.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* System Info */}
              <>
                <div className="sm:col-span-2 border-t pt-4">
                  <h3 className="font-semibold text-lg mb-3 mt-4">Impostazioni sistema</h3>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_public_visible"
                    checked={formData.is_public_visible || false}
                    onCheckedChange={(checked) => handleFieldChange('is_public_visible', checked)}
                    disabled={!editMode}
                  />
                  <Label htmlFor="is_public_visible">Profilo pubblico</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="in_voting"
                    checked={formData.in_voting || false}
                    onCheckedChange={(checked) => handleFieldChange('in_voting', checked)}
                    disabled={!editMode}
                  />
                  <Label htmlFor="in_voting">In votazione</Label>
                </div>
                <p className="text-xs text-gray-500 sm:col-span-2">
                  Spunta questa casella per mettere l'utente in votazione. Apparirà nella sezione "Votazioni in corso" della home page per gli utenti con diritto di voto.
                </p>

                {!createMode && (
                  <div className="sm:col-span-2">
                    <details className="border rounded-md p-3">
                      <summary className="cursor-pointer font-medium">
                        Ultimi 10 accessi
                      </summary>
                      <div className="mt-3 overflow-x-auto">
                        {memberAccessLogs.length === 0 ? (
                          <p className="text-sm text-gray-500">Nessun accesso registrato</p>
                        ) : (
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b text-left">
                                <th className="py-2 pr-2 font-medium">Data/Ora</th>
                                <th className="py-2 pr-2 font-medium">IP</th>
                              </tr>
                            </thead>
                            <tbody>
                              {memberAccessLogs.map((logItem, idx) => (
                                <tr key={`${logItem.login_at || 'n/a'}-${idx}`} className="border-b last:border-b-0">
                                  <td className="py-2 pr-2">{formatLoginDateTime(logItem.login_at)}</td>
                                  <td className="py-2 pr-2">{logItem.ip_address || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </details>
                  </div>
                )}

                {/* Metodo di Autenticazione */}
                <div className="sm:col-span-2">
                  <Label>Metodo di autenticazione</Label>
                  <div className="flex gap-4 mt-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="auth_password"
                        name="auth_method"
                        value="password"
                        checked={formData.auth_method === 'password'}
                        onChange={(e) => handleFieldChange('auth_method', e.target.value)}
                        className="w-4 h-4"
                        disabled={!editMode}
                      />
                      <Label htmlFor="auth_password" className={`font-normal ${editMode ? 'cursor-pointer' : ''}`}>
                        Password
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="auth_otp"
                        name="auth_method"
                        value="otp_email"
                        checked={formData.auth_method === 'otp_email'}
                        onChange={(e) => handleFieldChange('auth_method', e.target.value)}
                        className="w-4 h-4"
                        disabled={!editMode}
                      />
                      <Label htmlFor="auth_otp" className={`font-normal ${editMode ? 'cursor-pointer' : ''}`}>
                        OTP Email
                      </Label>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.auth_method === 'otp_email' 
                      ? 'L\'utente riceverà un codice OTP via email per accedere' 
                      : 'L\'utente userà email e password per accedere'}
                  </p>
                </div>

                {/* Reset Password - Solo per auth_method = password */}
                {formData.auth_method === 'password' && !createMode && (
                  <div className="sm:col-span-2 border-t pt-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                      <Label className="text-base font-semibold">Reset Password</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPasswordReset(!showPasswordReset)}
                        disabled={!editMode}
                      >
                        {showPasswordReset ? 'Annulla' : 'Reimposta Password'}
                      </Button>
                    </div>
                    
                    {showPasswordReset && editMode && (
                      <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <Label htmlFor="new_password">Nuova Password</Label>
                          <Input
                            id="new_password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Inserisci nuova password (min 8 caratteri)"
                          />
                        </div>
                        <div>
                          <Label htmlFor="confirm_password">Conferma Password</Label>
                          <Input
                            id="confirm_password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Conferma la nuova password"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={handleResetPassword}
                          disabled={resettingPassword}
                          className="w-full"
                        >
                          {resettingPassword ? 'Reimpostazione...' : 'Conferma reset password'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </>
            </div>
          )}

          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setModalOpen(false)} className="w-full sm:w-auto">
              {editMode ? 'Annulla' : 'Chiudi'}
            </Button>
            {editMode && (
              <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
                {saving ? 'Salvataggio...' : 'Salva modifiche'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ProfilePhotoCropModal
        open={cropModalOpen}
        onOpenChange={(open) => {
          setCropModalOpen(open);
          if (!open) {
            setRawProfilePhotoFile(null);
          }
        }}
        file={isReadOnlyMemberModal ? null : rawProfilePhotoFile}
        onConfirm={isReadOnlyMemberModal ? undefined : handleCropConfirm}
      />

      {/* Quote Associative Modal */}
      <Dialog 
        open={quoteModalOpen} 
        onOpenChange={(open) => {
          setQuoteModalOpen(open);
          if (!open) {
            setSelectedMemberForQuote(null);
            setQuote([]);
            setEditingQuota(null);
            setShowQuotaForm(false);
            setQuoteFormData(getInitialQuoteFormData());
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-1rem)] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Quote associative</DialogTitle>
            <DialogDescription>
              {selectedMemberForQuote && `Gestisci le quote associative per ${selectedMemberForQuote.first_name} ${selectedMemberForQuote.last_name}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Form creazione/modifica quota (solo se USER_EDIT_QUOTE) */}
            {hasPermission('USER_EDIT_QUOTE') && (
              <div className="space-y-3">
                {!showQuotaForm && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditingQuota(null);
                      setQuoteFormData(getInitialQuoteFormData(selectedMemberForQuote?.id));
                      setShowQuotaForm(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    AGGIUNGI QUOTA
                  </Button>
                )}
                {showQuotaForm && (
                  <div className="p-4 border rounded-lg bg-gray-50">
                    <h3 className="font-semibold mb-4">{editingQuota ? 'Modifica quota' : 'Nuova Quota'}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Data Inizio</Label>
                        <Input
                          type="date"
                          value={quoteFormData.data_inizio}
                          onChange={(e) => setQuoteFormData({...quoteFormData, data_inizio: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Data Fine</Label>
                        <Input
                          type="date"
                          value={quoteFormData.data_fine}
                          onChange={(e) => setQuoteFormData({...quoteFormData, data_fine: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Prezzo (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={quoteFormData.prezzo}
                          disabled={isEditingPaidQuota}
                          onChange={(e) => setQuoteFormData({...quoteFormData, prezzo: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Gruppo da assegnare automaticamente</Label>
                        <Select
                          value={quoteFormData.gruppo_id}
                          disabled={isEditingPaidQuota}
                          onValueChange={(value) => setQuoteFormData({...quoteFormData, gruppo_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona gruppo" />
                          </SelectTrigger>
                          <SelectContent>
                            {groups.map(g => (
                              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {(!editingQuota || !hasStripeSession(editingQuota)) ? (
                        <div className="sm:col-span-2 flex items-center space-x-2">
                          <Checkbox
                            id="pagato"
                            checked={quoteFormData.pagato}
                            onCheckedChange={(checked) => setQuoteFormData({ ...quoteFormData, pagato: checked === true })}
                          />
                          <Label htmlFor="pagato">Pagato</Label>
                        </div>
                      ) : (
                        <div className="sm:col-span-2 text-sm text-gray-600">
                          Pagamento gestito da Stripe: lo stato non è modificabile manualmente.
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <Button onClick={handleCreateQuota} className="w-full sm:w-auto">
                        {editingQuota ? 'Aggiorna' : 'Crea'} Quota
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => {
                          setEditingQuota(null);
                          setQuoteFormData(getInitialQuoteFormData(selectedMemberForQuote?.id));
                          setShowQuotaForm(false);
                        }}
                      >
                        Annulla
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Lista quote */}
            <div>
              <h3 className="font-semibold mb-4">Quote Esistenti</h3>
              {loadingQuote ? (
                <div className="text-center py-4">Caricamento...</div>
              ) : quote.length === 0 ? (
                <div className="text-center py-4 text-gray-500">Nessuna quota trovata</div>
              ) : (
                <div className="space-y-3">
                  {quote.map((q) => (
                    <Card
                      key={q.id}
                      className={`p-4 transition-colors ${editingQuota?.id === q.id ? 'bg-emerald-50 border-emerald-300' : ''}`}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{q.gruppo_name}</span>
                            <span className={`px-2 py-1 text-xs rounded ${q.pagato ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {q.pagato ? '✓ Pagato' : '⏳ In attesa'}
                            </span>
                            {getPaymentLabel(q) && (
                              <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                                {getPaymentLabel(q)}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>📅 Dal {new Date(q.data_inizio).toLocaleDateString('it-IT')} al {new Date(q.data_fine).toLocaleDateString('it-IT')}</p>
                            <p>💶 Prezzo: €{q.prezzo.toFixed(2)}</p>
                            {q.payment_details && (
                              <p className="text-xs">
                                💳 Pagamento: {q.payment_details.method} - {q.payment_details.status}
                              </p>
                            )}
                          </div>
                        </div>
                        {hasPermission('USER_EDIT_QUOTE') && (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditQuota(q)}
                              disabled={editingQuota?.id === q.id}
                              className={editingQuota?.id === q.id ? 'opacity-60 cursor-not-allowed' : ''}
                            >
                              Modifica
                            </Button>
                            {canRefundQuota(q) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRefundQuota(q)}
                                disabled={refundingQuotaId === q.id}
                                className="text-orange-600 border-orange-600 hover:bg-orange-50"
                              >
                                {refundingQuotaId === q.id ? 'Rimborso...' : 'Rimborsa'}
                              </Button>
                            )}
                            {canDeleteQuota(q) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteQuota(q.id)}
                                className="text-red-600 border-red-600 hover:bg-red-50"
                              >
                                Elimina
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={votingNotificationPromptOpen}
        onOpenChange={(open) => {
          if (sendingVotingNotification) return;
          setVotingNotificationPromptOpen(open);
          if (!open) {
            setPendingVotingNotificationMember(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>INVIO NOTIFICHE</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi inviare una notifica a tutti gli utenti con diritto di voto che li avvisa del fatto che c&apos;è un nuovo membro sottoposto a votazione?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingVotingNotification}>NO</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendVotingNotification} disabled={sendingVotingNotification}>
              {sendingVotingNotification ? 'Invio...' : 'SI'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
